export interface LoginResponse {
  ok: true;
}

export interface ApiError {
  error: string;
  code?: string;
}

export interface UserMe {
  email: string;
  credits: number;
  extraCredits: number;
}

// ============================================================
// Billing
// ============================================================

export type SubscriptionPlan = 'starter' | 'growth' | 'scale';
export type SubscriptionStatus = 'active' | 'canceling' | 'canceled' | 'past_due';

export interface SubscriptionInfo {
  plan: SubscriptionPlan;
  status: SubscriptionStatus;
  creditsPerMonth: number;
  currentPeriodEnd: string | null;
}

export interface CreditPurchase {
  id: string;
  creditsAdded: number;
  amountCents: number;
  createdAt: string;
}

export interface BillingInfo {
  subscription: SubscriptionInfo | null;
  purchases: CreditPurchase[];
}

export type CheckoutSessionResponse = { url: string } | { upgraded: true };

export class ApiClientError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

// ============================================================
// Generations
// ============================================================

export type GenerationStatus = 'pending' | 'processing' | 'completed' | 'failed';
export type GenerationType = 'e-commerce' | 'general';

export interface GenerationListItem {
  id: string;
  productUrl: string;
  title: string | null;
  description: string | null;
  status: GenerationStatus;
  imageUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface PaginatedGenerations {
  items: GenerationListItem[];
  pagination: Pagination;
}

export interface ArticleSection {
  heading: string;
  content: string;
  table?: string;
}

export interface ArticleFaq {
  question: string;
  answer: string;
}

export interface ArticleOutput {
  title: string;
  introduction: string;
  h2_sections: ArticleSection[];
  faqs: ArticleFaq[];
  conclusion: string;
}

export interface SocialMediaOutput {
  instagram_posts: string[];
  linkedin_posts: string[];
  facebook_posts: string[];
  x_posts: string[];
}

export interface GenerationDetail {
  id: string;
  productUrl: string;
  title: string | null;
  description: string | null;
  status: GenerationStatus;
  blogArticle: string | null;
  contentArticle: ArticleOutput | null;
  contentSocial: SocialMediaOutput | null;
  imageUrl: string | null;
  generalFeedback: string | null;
  imagePrompt: string | null;
  errorMessage: string | null;
  creditRefunded: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateGenerationRequest {
  type?: GenerationType;
  url?: string;
  scrapedTitle?: string;
  scrapedDescription?: string;
}

export interface CreateGenerationResponse {
  generationId: string;
}

// ============================================================
// Campaigns
// ============================================================

export type PhaseType = 'statu_quo' | 'perspective_shift' | 'objections' | 'climax';
export type CampaignStatus = 'planning' | 'active' | 'failed';

export interface CampaignRoadmapPhase {
  phase: number;
  phase_type: PhaseType;
  generations: string[];
  strategic_angle: string;
  narrative_hook: string;
  evolution_goal: string;
}

export interface CampaignMasterPlan {
  campaign_meta: {
    global_objective: string;
    target_psychology: string;
    rogue_tone_rules: string[];
  };
  roadmap: CampaignRoadmapPhase[];
}

export interface CampaignListItem {
  id: string;
  topic: string;
  targetAudience: string;
  finalObjective: string;
  status: CampaignStatus;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PaginatedCampaigns {
  items: CampaignListItem[];
  pagination: Pagination;
}

export interface CampaignGenerationSummary {
  id: string;
  campaignPhase: number | null;
  status: string;
  title: string | null;
  description: string | null;
  imageUrl: string | null;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CampaignDetail {
  id: string;
  topic: string;
  targetAudience: string;
  finalObjective: string;
  masterPlan: CampaignMasterPlan | null;
  status: CampaignStatus;
  errorMessage: string | null;
  generations: CampaignGenerationSummary[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateCampaignRequest {
  topic: string;
  targetAudience: string;
  finalObjective: string;
}

export interface CreateCampaignResponse {
  campaignId: string;
}

export interface EditRoadmapPhaseRequest {
  strategicAngle?: string;
  narrativeHook?: string;
  evolutionGoal?: string;
}
